import { logError } from "./logger";

export const createPackageOrderMap = packageConfig => packageConfig?.reduce((map, pkg, index) => {
  if (pkg.order) {
    map[pkg.id] = parseInt(pkg.order, 10);
  } else {
    map[pkg.id] = index;
  }
  return map;
}, {});

export const sortGenrepack = (
  genrePackages,
  packageConfig,
  component = undefined
) => {
  try {
    const packageOrderMap = createPackageOrderMap(packageConfig);
    const parsedOptions = Array.isArray(genrePackages.options) ?
      genrePackages.options.map(option => {
        const [name, packageId] = option.split(",");
        return {
          name,
          packageId: packageId?.trim()
        };
      }) :
      Array.isArray(genrePackages) ?
        genrePackages.map(pkg => {
          if (typeof pkg === "string") {
            return { packageId: pkg?.trim() };
          }
          return {
            ...pkg,
            packageId: (pkg?.packageId ?? pkg?.packageKey ?? "").trim()
          };
        }) :
        [];
    const normalizePackageId = packageId => (packageId.startsWith("ATT_TV_") ? packageId : `ATT_TV_${packageId}`);
    const sortedOptions = parsedOptions
      .filter(option => {
        const normalizedPackageId = normalizePackageId(option?.packageId);
        const exists = packageOrderMap[normalizedPackageId] !== undefined;
        return exists;
      })
      .sort((a, b) => {
        const orderA = packageOrderMap[normalizePackageId(a?.packageId)];
        const orderB = packageOrderMap[normalizePackageId(b?.packageId)];
        return orderA - orderB;
      });
    // Extract the sorted packageId values into genreSortKeyArray
    const genreSortKeyArray = sortedOptions.map(option => option.packageId);
    if (component === "channelpacklineup") {
      genrePackages.sort((a, b) => {
        const orderA = packageOrderMap[a?.packageKey];
        const orderB = packageOrderMap[b?.packageKey];
        return orderA !== undefined && orderB !== undefined ?
          orderA - orderB :
          orderA !== undefined ?
            -1 :
            orderB !== undefined ?
              1 :
              0;
      });
      return genrePackages;
    }
    return genreSortKeyArray;
  } catch (e) {
    logError("GenreHelper", e, { method: "sortGenrePack" });
    return genrePackages; // Return unsortedgenrePackages in case of an error
  }
};

export const addGenrePackages = (
  packages,
  genrePackages,
  packageConfig
) => {
  const addPackages = packagesList => {
    packagesList?.forEach?.(genrePkg => {
      packages.push(genrePkg);
    });
  };

  if (!packageConfig) {
    addPackages(genrePackages);
    return packages;
  }

  const packageOrderMap = createPackageOrderMap(packageConfig);
  const sortedGenrePackages = genrePackages
    .filter(genrePkg => Object.prototype.hasOwnProperty.call(packageOrderMap, genrePkg?.packageId))
    .sort(
      (a, b) => packageOrderMap[a?.packageId] - packageOrderMap[b?.packageId]
    );

  addPackages(sortedGenrePackages);
  return packages;
};

export const extractGenreTooltips = genrePackages => {
  try{
    return genrePackages?.reduce((acc, genrePackage) => {
      if (genrePackage?.packageId && genrePackage?.tooltip) {
        acc[genrePackage.packageId] = genrePackage.tooltip;
      }
      return acc;
    }, {});
  } catch(_e){
    return {};
  }
}

// Generated by Copilot- Start
/**
 * Get package IDs by type from packageConfig
 * @param {Object} params - Parameters object
 * @param {string} params.type - Package type (e.g., "internet", "satellite", "genre", "genre-mini", "myfree")
 * @param {Array} params.packageConfig - Array of package objects
 * @returns {Array<string>} Array of package IDs
 * @example
 * // Returns: ["ATT_TV_MYSPORTS", "ATT_TV_MYENTERTAINMENT", "ATT_TV_MYKIDS"]
 * getPackageIdsByType({ type: "genre", packageConfig })
 */
export const getPackageIdsByType = ({ type, packageConfig }) => {
  try {
    if (!type || !Array.isArray(packageConfig)) {
      return [];
    }

    return packageConfig
      .filter(pkg => pkg?.type === type)
      .map(pkg => pkg?.id)
      .filter(Boolean);
  } catch (e) {
    logError("GenreHelper", e, { method: "getPackageIdsByType" });
    return [];
  }
}

/**
 * Build genre mapping object from packageConfig (queryparam -> short name without ATT_TV_ prefix)
 * Used for genre pack identification and filtering
 * @param {Object} params - Parameters object
 * @param {Array} params.packageConfig - Array of package objects
 * @returns {Object} Object with queryparam as key and short package name as value
 * @example
 * // Returns: { mysports: "MYSPORTS", myentertainment: "MYENTERTAINMENT", myhometeam: "MYHOMETEAM" }
 * getGenreMapping({ packageConfig })
 */
export const getGenreMapping = ({ packageConfig }) => {
  try {
    if (!Array.isArray(packageConfig)) {
      return {};
    }

    const genreIds = packageConfig.filter(pkg => pkg?.type === "genre" || pkg?.type === "genre-mini");
    const genreMapping = {};

    genreIds.forEach(pkg => {
      if (pkg?.queryparam && pkg?.id) {
        // Remove ATT_TV_ prefix from package ID
        const shortName = pkg.id.replace(/^ATT_TV_/, "");
        genreMapping[pkg.queryparam] = shortName;
      }
    });

    return genreMapping;
  } catch (e) {
    logError("GenreHelper", e, { method: "getGenreMapping" });
    return {};
  }
}

/**
 * Build genre to package mapping object from packageConfig (queryparam -> full package ID)
 * Used for mapping genre queryparams to full package identifiers
 * @param {Object} params - Parameters object
 * @param {Array} params.packageConfig - Array of package objects
 * @returns {Object} Object with queryparam as key and full package ID as value
 * @example
 * // Returns: { mysports: "ATT_TV_MYSPORTS", myentertainment: "ATT_TV_MYENTERTAINMENT", myhometeam: "ATT_TV_MYHOMETEAM" }
 * getGenreToPackageMapping({ packageConfig })
 */
export const getGenreToPackageMapping = ({ packageConfig }) => {
  try {
    if (!Array.isArray(packageConfig)) {
      return {};
    }

    const genreIds = packageConfig.filter(pkg => pkg?.type === "genre" || pkg?.type === "genre-mini");
    const genreToPackageMapping = {};

    genreIds.forEach(pkg => {
      if (pkg?.queryparam && pkg?.id) {
        genreToPackageMapping[pkg.queryparam] = pkg.id;
      }
    });

    return genreToPackageMapping;
  } catch (e) {
    logError("GenreHelper", e, { method: "getGenreToPackageMapping" });
    return {};
  }
}
// Generated by Copilot- End
